<template>
  <!-- phone-flush: no telefone o painel vai de borda a borda, com o respiro dele (ver InstanceCard) -->
  <ontos-base v-bind="baseProps" ref="base" phone-flush>
    <!-- O MESMO editor do modal: formulário, diff, confirmação de vínculo roubado e tradução
         dos erros do servidor. Esta página tinha uma segunda implementação, que gravava direto
         — sem a confirmação e sem as mensagens. Aqui ficaram só as decisões da página: de onde
         vem a instância, os botões do cabeçalho e para onde ir ao salvar. -->
    <ontos-instance-edit v-if="loaded"
                         :context="$route.params"
                         :idea="idea"
                         :instance="instance"
                         :domains="domains"
                         :choice-domains="workspace.domains"
                         :reverse-info="reverseInfo"
                         :original="original"
                         @saved="onSaved"
                         @showMessage="$emit('showMessage', $event)"
                         @showErrorMessage="$emit('showErrorMessage', $event)">
      <!-- Editar é um formulário, e de um formulário só se sai de duas maneiras: gravando ou
           desistindo. Ver a ficha, ir para a listagem e excluir não eram saídas daqui — eram
           atalhos para outras telas, com o rascunho sumindo no caminho sem aviso. Quem quer
           excluir faz isso na ficha, que é de onde se chega aqui. -->
      <!-- O Design não mora mais aqui: está na faixa do caminho e na barra do app (ver o App) -->
      <template #controls="{dirty}">
        <ontos-header-action icon="mdi-arrow-left" :label="$tt('back')" @click="onBack(dirty)"/>
      </template>
    </ontos-instance-edit>

    <!-- O mesmo aviso da seta do modal: aqui a janela é a rota, então Voltar é navegar — mas a
         perda, para quem está digitando, é idêntica. -->
    <ontos-unsaved-changes-dialog :leaving="leaving"
                                  @confirmed="goToView()" @cancelled="leaving = null"/>
  </ontos-base>
</template>
<script lang="ts">
import {Component, toNative} from 'vue-facing-decorator'
import InstanceEdit from '@/components/live/instance/InstanceEdit.vue'
import {handOver, takeHandover} from "@/utils/handover";
import {instanceKey, instanceViewKey} from "@/utils/instanceLink";
import {Preview, previewFromSnapshot, VIEW_READ, ViewRead} from "@/utils/instancePreview";
import {deepCopy} from "@/utils/copy";
import {knownForCreate, offerKnown} from "@/utils/instanceCreation";
import IdeaLevelRouteComponent from "@/components/shared/IdeaLevelRouteComponent";
import {instanceEdit} from "@/utils/heading";
import Instance, {emptyInstance} from "@/types/Instance";
import UnsavedChangesDialog, {Leave} from "@/components/shared/UnsavedChangesDialog.vue";

@Component({
  emits: ['crumb'],
  components: {
    'ontos-instance-edit': InstanceEdit,
    'ontos-unsaved-changes-dialog': UnsavedChangesDialog
  }
})
class EditInstance extends IdeaLevelRouteComponent{
      instance: Instance = emptyInstance()
      /** O item como está gravado, quando a `instance` chega já mexida pelo Expandir da janela. */
      original: Instance | null = null
      /** O item como está gravado, antes de o formulário mexer nele — ver `goToView`. */
      snapshot: ViewRead = {value: emptyInstance()}
      domains = {}
      reverseInfo: any = null
      /** Saída esperando confirmação, ou null quando não há aviso na tela */
      leaving: Leave | null = null

      /**
       * O mesmo cabeçalho do modal de editar: a regra mora no utils/heading.
       *
       * O id sai da rota, que é quem sabe qual item está aberto — o documento carregado não
       * traz `_instance`, e é dele que sai a cor do acento.
       */
      get heading() {
        return instanceEdit(this.instance, this.idea, this.$route.params.instance as string)
      }
      // createRel = false
      // createRelName = ''
      // createRelContext = null
      // createRelProperty = null

  /**
   * A instância, os candidatos e os metadados dos reversos — entregues pela ficha, ou lidos.
   *
   * A ficha de onde se veio já tinha tudo isto, e relê-lo custaria a requisição mais cara da
   * tela. Mas esta rota precisa valer sozinha — URL direta, F5, voltar, Expandir vindo do modal
   * —, então a entrega é um atalho com queda para o servidor, e nunca um requisito.
   */
  async load() {
      try {
        const {system: systemAlias, idea: ideaAlias, instance: instanceId} = this.params
        //A entrega e a leitura devolvem a MESMA forma, então daqui para baixo não há dois
        //caminhos — é o que impede os dois de divergirem com o tempo.
        //O `original` só vem pelo Expandir da janela, com o rascunho no `instance`
        const {instance, original, domains, reverseInfo} =
            takeHandover(instanceKey(this.params as any)) || await this.fromServer()
        this.instance = instance
        this.original = original || null
        //Como o item está gravado: é o que a ficha mostra ao voltar sem salvar. Do rascunho, ela
        //mostraria como gravado o que se descartou
        this.snapshot = {value: deepCopy(original || instance), relDomains: domains, reverseInfo}
        //Identidade junto do texto: o App compara com a rota antes de usar o rótulo
        //(ver instanceLabel no App.vue).
        this.$emit('crumb', {
          system: systemAlias, idea: ideaAlias, instance: instanceId, text: this.heading.title
        })
        this.domains = domains
        this.reverseInfo = reverseInfo
      } catch (err) {
        if (err.response && err.response.status === 404) this.$router.push({name: 'home'})
        else this.throwError(this.$tt('error.view-item', {params: JSON.stringify(this.params)}), err)
      }
      //Antes -50, agora 350
    }

    /**
     * O que esta edição leu da coleção, oferecido a quem criar nela — o "Criar" da faixa do caminho
     * mora no casco, e é por aqui que a janela abre sem ir ao servidor. Ver o `offerKnown`.
     *
     * Só depois de carregada: antes disso os candidatos são o `{}` do começo, que diria "não há
     * candidato nenhum".
     */
    private stopOffering: (() => void) | null = null

    created() {
      this.stopOffering = offerKnown(this.params.idea, () => this.loaded
          ? knownForCreate(this.workspace, this.params.idea, this.domains, this.reverseInfo) : null)
    }

    beforeUnmount() {
      this.stopOffering?.()
    }

    /**
     * A leitura de sempre, na forma que a entrega também tem: a MESMA da ficha (`VIEW_READ`), para o que
     * a edição guarda servir de prévia à ficha ao voltar. `failures` entra nela: o que o item já viola,
     * que o formulário avisa nos campos não mexidos.
     */
    private async fromServer() {
      const {system: systemAlias, idea: ideaAlias, instance: instanceId} = this.params
      const res = await this.$instances.get({systemAlias, ideaAlias, instanceId: Number(instanceId)}, VIEW_READ)
      return {instance: res.value, domains: res.relDomains, reverseInfo: res.reverseInfo}
    }

    /**
     * Salvou: a janela aqui é a rota, então voltar para a visualização é navegar — com o que se salvou,
     * para a ficha já mostrar. O item que quem salvou deixou de ver (confirmado no `AccessLossDialog`)
     * não tem ficha para ele — a coleção.
     */
    async onSaved(_instance: unknown, {hidden, preview}: {hidden?: boolean, preview?: Preview} = {}) {
      if (hidden) await this.$router.push({name: 'collection', params: {system: this.params.system, idea: this.params.idea}})
      else await this.goToView(preview || null)
    }

    /** Desistiu: a ficha de volta, com o aviso quando há rascunho — como a seta do modal. */
    onBack(dirty: boolean) {
      if (dirty) this.leaving = 'back'
      else this.goToView()
    }

    /**
     * A ficha de volta, já com o que se tem: sem salvar, o item como chegou (o rascunho descartado não
     * conta); salvando, o que o formulário montou — ou `null`, quando o título mudaria e só o servidor o
     * sabe.
     */
    async goToView(preview: Preview | null = previewFromSnapshot(this.snapshot)) {
      this.leaving = null
      if (preview) handOver(instanceViewKey(this.params as any), preview)
      await this.$router.push({name: 'view-instance', params: this.params})
    }

}



export default toNative(EditInstance)
</script>
